import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import {
  registerIndividual,
  verifyEmailForEmployeeSelfRegistration,
  verifyEmailOtpForEmployeeSelfRegistration,
  verifyMobileForEmployeeSelfRegistration,
  verifyMobileOtpForEmployeeSelfRegistration,
} from '../../common/fetch';
import { cancelMsg, confirmMsg, continueMsg, t } from '../../common/i18n';
import { FORM_GRID_FORCE_END, FormGridFieldset } from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio';
import { Email, PhoneNumber } from '../../common/react-hook-form/fields';
import { SubscriptionType } from '../../generated/api/subscriptionType';
import { useAuth } from '../../public/site/AuthProvider';
import { VerifyOtp } from './VerifyOtp';
import type { AccountWithSubscriptions } from '../../generated/api/accountWithSubscriptions';
import type { OnlineApiError } from '../../generated/api/onlineApiError';
import type { SubscriptionSearchConflictResponse } from '../../generated/api/subscriptionSearchConflictResponse';

import './EoeSelfRegistration.scss';

export enum EmployeeSelfRegistrationStateEnum {
  INITIATE_EMAIL_VERIFICATION = 1,
  VERIFY_EMAIL_OTP = 2,
  INITIATE_MOBILE_VERIFICATION = 3,
  VERIFY_MOBILE_OTP = 4,
  NO_ACCOUNT_FOUND = 5,
  MULTIPLE_ACCOUNTS_FOUND = 6,
  CONTACT_ADMIN = 7,
}

export interface MobileOtpSelfRegistrationResponse {
  verified: boolean;
  subscriptionSearchConflictResponse?: SubscriptionSearchConflictResponse;
}

const EmployeeSelfRegistrationStateKey = 'employee-self-registration-state';

export interface EmployeeSelfRegistrationState {
  state: EmployeeSelfRegistrationStateEnum;
  email?: string;
  emailAlreadyVerified?: boolean;
  phoneNumber?: string;
  validUntil?: number;
  accountWithSubscriptions?: AccountWithSubscriptions[];
}

export const getEmployeeSelfRegistrationStateFromSession = (): EmployeeSelfRegistrationState | undefined => {
  const stateString = sessionStorage.getItem(EmployeeSelfRegistrationStateKey);
  if (stateString) {
    return JSON.parse(stateString) as EmployeeSelfRegistrationState;
  }
  return undefined;
};

const setEmployeeSelfRegistrationStateToSession = (state: EmployeeSelfRegistrationState) => {
  sessionStorage.setItem(EmployeeSelfRegistrationStateKey, JSON.stringify(state));
};

const clearEmployeeSelfRegistrationStateFromSession = () => {
  sessionStorage.removeItem(EmployeeSelfRegistrationStateKey);
};

interface InitiateEmailVerificationProps {
  requestVerifyEmail: (email: string) => void;
  sending: boolean;
}

const InitiateEmailVerification = ({ requestVerifyEmail, sending }: InitiateEmailVerificationProps) => {
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values: { email: string }) => {
    requestVerifyEmail(values.email);
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <FormGridFieldset>
            <h3 className={dsClass.MARGIN_TOP_0}>{t.RXQO('Confirm your email address.')}</h3>
            <p className={dsClass.MARGIN_TOP_0}>
              {t.XONV('Start Employee OmaElisa activation by confirming your email address.')}
            </p>
            <Email
              label={t.R9VH('Email address (work)')}
              readonly={false}
              errorText={t.NJLM('Check the email address. Enter the address in the format name@example.com')}
            />
            <CL.Button type="submit" size="m" loading={sending}>
              {t.QVYK(confirmMsg)}
            </CL.Button>
          </FormGridFieldset>
        </form>
      </FormProvider>
    </div>
  );
};

interface InitiateMobileVerificationProps {
  requestVerifyMobile: (email: string) => void;
  sending: boolean;
  emailAlreadyVerified?: boolean;
}

const InitiateMobileVerification = ({
  requestVerifyMobile,
  sending,
  emailAlreadyVerified,
}: InitiateMobileVerificationProps) => {
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      phoneNumber: '',
    },
  });

  const onSubmit = async (values: { phoneNumber: string }) => {
    requestVerifyMobile(values.phoneNumber);
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <FormGridFieldset>
            <h3 className={dsClass.MARGIN_TOP_0}>{t.H56I('Confirm your phone number.')}</h3>
            {emailAlreadyVerified && (
              <p className={dsClass.MARGIN_TOP_0}>
                {t.TSNL(
                  'Your email address has already been confirmed. Please continue by confirming your phone number.'
                )}
              </p>
            )}
            <PhoneNumber label={t.DIRC('Phone number (work)')} readonly={false} />
            <CL.Button type="submit" size="m" loading={sending}>
              {t.QVYK(confirmMsg)}
            </CL.Button>
          </FormGridFieldset>
        </form>
      </FormProvider>
    </div>
  );
};

interface ErrorNotificationProps {
  title: string;
  message: string;
  onCancel: () => void;
}

const ErrorNotification = ({ title, message, onCancel }: ErrorNotificationProps) => {
  return (
    <CL.Grid>
      <CL.GridRow>
        <h3 className={dsClass.MARGIN_0}>{title}</h3>
      </CL.GridRow>
      <CL.GridRow>
        <p>{message}</p>
      </CL.GridRow>
      <CL.GridRow>
        <CL.Button size="m" onClick={onCancel} className={dsClass.WIDTH_100}>
          {t.B2V1(cancelMsg)}
        </CL.Button>
      </CL.GridRow>
    </CL.Grid>
  );
};

interface MultipleCompaniesFoundProps {
  accountWithSubscriptions: AccountWithSubscriptions[];
  selectCompany: (mdmId: string) => void;
  onCancel: () => void;
}

const MultipleCompaniesFound = ({ accountWithSubscriptions, selectCompany, onCancel }: MultipleCompaniesFoundProps) => {
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      mdmId: accountWithSubscriptions[0]?.mdmId || '',
    },
  });

  const onSubmit = async (values: { mdmId: string }) => {
    selectCompany(values.mdmId);
  };

  const getRadioButtonProps = (accountWithSubscription: AccountWithSubscriptions) => ({
    children: (
      <div className={dsClass.DISPLAY_BLOCK}>
        <b>{accountWithSubscription.accountName}</b>
        {accountWithSubscription.subscriptions.map(subscription => {
          const subscriptionTypeMsg =
            subscription.subscriptionType === SubscriptionType.MOBILE ? t.D2UO('subscription') : t.ACH1('device');
          return (
            <div key={subscription.subscriptionId}>
              <p
                className={dsClass.MARGIN_0}
              >{`${accountWithSubscription.accountName} ${t.YBF8('owns the')} ${subscriptionTypeMsg}: ${subscription.subscriptionName}`}</p>
            </div>
          );
        })}
      </div>
    ),
    value: accountWithSubscription.mdmId,
  });

  const radioButtonOptions = accountWithSubscriptions.map(accountWithSubscription =>
    getRadioButtonProps(accountWithSubscription)
  );

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <FormGridFieldset>
            <h3 className={dsClass.MARGIN_0}>
              {t.RLNC(`Select which company's Employee OmaElisa do you want to use?`)}
            </h3>
            <p>{t.V90E('We found your products in more than one company.')}</p>
            <SelectRadio name="mdmId" items={radioButtonOptions} defaultValue={accountWithSubscriptions[0]?.mdmId} />
            <div className={`${FORM_GRID_FORCE_END} ${dsClass.TEXT_ALIGN_END}`}>
              <CL.Button color="link" size="m" onClick={onCancel}>
                {t.B2V1('Cancel')}
              </CL.Button>
              <CL.Button type="submit" size="m">
                {t.I62A(continueMsg)}
              </CL.Button>
            </div>
          </FormGridFieldset>
        </form>
      </FormProvider>
    </div>
  );
};

const ErrorDisclaimer = () => {
  return (
    <CL.Disclaimer
      text={t.GEPH(
        'Unfortunately, an error occurred in our system. Please try again shortly.We apologize for the inconvenience.'
      )}
      disclaimerType="alert"
      icon={<CL.Icon icon="warning" color="error" size="l" />}
      className={`${dsClass.MARGIN_TOP_4} `}
    />
  );
};

export interface EmployeeSelfRegistrationProps {
  employeeSelfRegistrationState?: EmployeeSelfRegistrationState;
}

export const EoeSelfRegistration = ({ employeeSelfRegistrationState }: EmployeeSelfRegistrationProps) => {
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [currentState, setCurrentState] = useState<EmployeeSelfRegistrationState>(
    employeeSelfRegistrationState ?? {
      state: EmployeeSelfRegistrationStateEnum.INITIATE_EMAIL_VERIFICATION,
    }
  );
  const [expiredOtp, setExpiredOtp] = useState(false);
  const { validateAuth, logout } = useAuth();
  const navigate = useNavigate();

  const setNextStateAndSession = (state: EmployeeSelfRegistrationState, errored?: boolean) => {
    setCurrentState(state);
    setEmployeeSelfRegistrationStateToSession(state);
    setIsError(!!errored);
  };

  const requestVerifyEmail = async (email: string) => {
    setLoading(true);
    try {
      await verifyEmailForEmployeeSelfRegistration({ email: email }, false);
      const verifyEmailState: EmployeeSelfRegistrationState = {
        state: EmployeeSelfRegistrationStateEnum.VERIFY_EMAIL_OTP,
        email: email,
        validUntil: Date.now() + 10 * 60 * 1000, // valid for 10 minutes
      };
      setNextStateAndSession(verifyEmailState);
    } catch (error) {
      if (error instanceof Response && error.status === 409) {
        const onlineApiError: OnlineApiError | null = await error.json().catch(() => null);
        if (onlineApiError?.error?.toLowerCase().includes('email already verified')) {
          const initiateMobileVerificationState: EmployeeSelfRegistrationState = {
            state: EmployeeSelfRegistrationStateEnum.INITIATE_MOBILE_VERIFICATION,
            email: email,
            emailAlreadyVerified: true,
          };
          setNextStateAndSession(initiateMobileVerificationState);
          return;
        }
      }
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const resendVerifyEmail = async (): Promise<boolean> => {
    try {
      const email = currentState!.email!;
      await verifyEmailForEmployeeSelfRegistration({ email: email }, true);
      const verifyEmailState: EmployeeSelfRegistrationState = {
        state: EmployeeSelfRegistrationStateEnum.VERIFY_EMAIL_OTP,
        email: email,
        validUntil: Date.now() + 10 * 60 * 1000, // valid for 10 minutes
      };
      setNextStateAndSession(verifyEmailState);
      return true;
    } catch (error) {
      setIsError(true);
      return false;
    } finally {
      setExpiredOtp(false);
      setLoading(false);
    }
  };

  const verifyEmailOtp = async (otp: string) => {
    setLoading(true);
    if (currentState.validUntil && Date.now() > currentState.validUntil) {
      setExpiredOtp(true);
      setLoading(false);
      return;
    }
    try {
      await verifyEmailOtpForEmployeeSelfRegistration({ email: currentState.email!, otp: otp });
      const initiateMobileVerificationState: EmployeeSelfRegistrationState = {
        state: EmployeeSelfRegistrationStateEnum.INITIATE_MOBILE_VERIFICATION,
        email: currentState.email,
      };
      setNextStateAndSession(initiateMobileVerificationState);
    } catch (error) {
      if (error instanceof Response && error.status === 409) {
        const onlineApiError: OnlineApiError | null = await error.json().catch(() => null);
        if (onlineApiError?.error?.toLowerCase().includes('otp is expired')) {
          setExpiredOtp(true);
          return;
        }
      }
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const goBackToInput = () => {
    const previousState =
      currentState.state === EmployeeSelfRegistrationStateEnum.VERIFY_MOBILE_OTP
        ? EmployeeSelfRegistrationStateEnum.INITIATE_MOBILE_VERIFICATION
        : EmployeeSelfRegistrationStateEnum.INITIATE_EMAIL_VERIFICATION;
    const initiateEmailVerificationState: EmployeeSelfRegistrationState = {
      state: previousState,
    };
    setNextStateAndSession(initiateEmailVerificationState);
  };

  const requestVerifyMobile = async (phoneNumber: string): Promise<boolean> => {
    setLoading(true);
    try {
      await verifyMobileForEmployeeSelfRegistration({ mobileNumber: phoneNumber });
      const verifyMobileState: EmployeeSelfRegistrationState = {
        state: EmployeeSelfRegistrationStateEnum.VERIFY_MOBILE_OTP,
        email: currentState.email,
        phoneNumber: phoneNumber,
        validUntil: Date.now() + 10 * 60 * 1000, // valid for 10 minutes
      };
      setNextStateAndSession(verifyMobileState);
      return true;
    } catch (error) {
      setNextStateAndSession(
        {
          state: EmployeeSelfRegistrationStateEnum.INITIATE_EMAIL_VERIFICATION,
        },
        true
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyMobileOtp = async (otp: string) => {
    setLoading(true);
    if (currentState.validUntil && Date.now() > currentState.validUntil) {
      setExpiredOtp(true);
      setLoading(false);
      return;
    }
    try {
      const mobileOtpSelfRegistrationResponse = await verifyMobileOtpForEmployeeSelfRegistration({
        mobileNumber: currentState.phoneNumber!,
        otp: otp,
      });
      if (mobileOtpSelfRegistrationResponse.verified) {
        validateAuth();
        clearEmployeeSelfRegistrationStateFromSession();
        navigate(paths.EMPLOYEE_HOME);
      } else {
        switch (mobileOtpSelfRegistrationResponse.subscriptionSearchConflictResponse?.type) {
          case 'NO_ACCOUNT_FOUND':
            setNextStateAndSession({
              state: EmployeeSelfRegistrationStateEnum.NO_ACCOUNT_FOUND,
            });
            break;
          case 'MULTIPLE_ACCOUNTS_FOUND':
            setNextStateAndSession({
              state: EmployeeSelfRegistrationStateEnum.MULTIPLE_ACCOUNTS_FOUND,
              accountWithSubscriptions:
                mobileOtpSelfRegistrationResponse.subscriptionSearchConflictResponse?.items || [],
            });
            break;
          case 'DUPLICATE_CONTACTS_FOUND':
            setNextStateAndSession({
              state: EmployeeSelfRegistrationStateEnum.CONTACT_ADMIN,
            });
            break;
          default:
            setIsError(true);
            break;
        }
      }
    } catch (error) {
      if (error instanceof Response && error.status === 400) {
        const onlineApiError: OnlineApiError | null = await error.json().catch(() => null);
        if (onlineApiError?.error?.toLowerCase().includes('invalid otp')) {
          setExpiredOtp(true);
          return;
        }
      }
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const registerSelectedCompany = async (mdmId: string) => {
    setLoading(true);
    try {
      await registerIndividual({
        accountMDMID: mdmId,
      });
      validateAuth();
      clearEmployeeSelfRegistrationStateFromSession();
      navigate(paths.EMPLOYEE_HOME);
    } catch (error) {
      if (error instanceof Response && error.status === 409) {
        const onlineApiError: OnlineApiError | null = await error.json().catch(() => null);
        if (onlineApiError?.error?.toLowerCase().includes('duplicate contact')) {
          setNextStateAndSession({
            state: EmployeeSelfRegistrationStateEnum.CONTACT_ADMIN,
          });
          return;
        }
      }
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    clearEmployeeSelfRegistrationStateFromSession();
    logout('', false, true);
  };

  const contentByState = () => {
    switch (currentState.state) {
      case EmployeeSelfRegistrationStateEnum.INITIATE_EMAIL_VERIFICATION:
        return <InitiateEmailVerification requestVerifyEmail={requestVerifyEmail} sending={loading} />;
      case EmployeeSelfRegistrationStateEnum.VERIFY_EMAIL_OTP:
        return (
          <VerifyOtp
            email={currentState.email}
            validUntil={currentState.validUntil!}
            verifying={loading}
            expiredOtp={expiredOtp}
            verifyOtp={verifyEmailOtp}
            resendOtp={resendVerifyEmail}
            goBack={goBackToInput}
          />
        );
      case EmployeeSelfRegistrationStateEnum.INITIATE_MOBILE_VERIFICATION:
        return (
          <InitiateMobileVerification
            requestVerifyMobile={requestVerifyMobile}
            sending={loading}
            emailAlreadyVerified={currentState.emailAlreadyVerified}
          />
        );
      case EmployeeSelfRegistrationStateEnum.VERIFY_MOBILE_OTP:
        return (
          <VerifyOtp
            phoneNumber={currentState.phoneNumber}
            validUntil={currentState.validUntil!}
            verifying={loading}
            expiredOtp={expiredOtp}
            verifyOtp={verifyMobileOtp}
            resendOtp={() => requestVerifyMobile(currentState.phoneNumber!)}
            goBack={goBackToInput}
          />
        );
      case EmployeeSelfRegistrationStateEnum.NO_ACCOUNT_FOUND:
        return (
          <ErrorNotification
            title={t.Z8VV('We couldn’t find any company with your information')}
            message={t.H3IT(
              'If necessary, contact your employer’s representative and ask them to send you an invitation to OmaElisa for Employees.'
            )}
            onCancel={onCancel}
          />
        );
      case EmployeeSelfRegistrationStateEnum.MULTIPLE_ACCOUNTS_FOUND:
        return (
          <MultipleCompaniesFound
            accountWithSubscriptions={currentState.accountWithSubscriptions || []}
            selectCompany={registerSelectedCompany}
            onCancel={onCancel}
          />
        );
      case EmployeeSelfRegistrationStateEnum.CONTACT_ADMIN:
        return (
          <ErrorNotification
            title={t.AHN7('We are unable to link your contact information to the company')}
            message={t.TW24(
              'Please contact your employer’s representative and ask them to check your personal and contact information in OmaElisa For companies, or, if necessary, add you as a user.'
            )}
            onCancel={onCancel}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="of-employee-self-registration__root">
      <div className="of-employee-self-registration__body">
        {contentByState()}
        {isError && <ErrorDisclaimer />}
      </div>
    </div>
  );
};
